import { useEffect, useMemo, useState } from 'react'
import { match } from 'ts-pattern'

import { Point } from '@blade/std-lib'

import { createRoundedConnectorPath } from './line-util'

export type ConnectionPosition = 'bottom-middle' | 'top-middle' | 'center'

type EdgeLineProps = {
  sourceEl: React.RefObject<HTMLElement | null> | HTMLDivElement
  targetEl: React.RefObject<HTMLElement | null> | HTMLDivElement
  rootEl: React.RefObject<HTMLElement | null>
  sourcePosition: ConnectionPosition
  targetPosition: ConnectionPosition
  variant?: 'placeholder' | 'default'
  direction?: 'horizontal-first' | 'vertical-first'
  radius?: number
}

export const EdgeLine: React.FC<EdgeLineProps> = ({
  sourceEl,
  targetEl,
  rootEl,
  sourcePosition,
  targetPosition,
  direction = 'horizontal-first',
  radius = 12,
  variant = 'default',
}) => {
  const [sourcePoint, setSourcePoint] = useState<Point.Point | undefined>()
  const [targetPoint, setTargetPoint] = useState<Point.Point | undefined>()

  useEffect(() => {
    const rootEl_ = 'current' in rootEl ? rootEl.current : rootEl

    const sourceEl_ = 'current' in sourceEl ? sourceEl.current : sourceEl
    if (!sourceEl_ || !rootEl_) {
      return
    }

    let rafId: number | undefined
    const loop = () => {
      const bbox = sourceEl_.getBoundingClientRect()
      const point = getPointPosition(
        bbox,
        rootEl_.getBoundingClientRect(),
        sourcePosition,
      )
      setSourcePoint((prev) => (prev && Point.eq(prev, point) ? prev : point))
      rafId = requestAnimationFrame(loop)
    }

    loop()

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [rootEl, sourceEl, sourcePosition])

  useEffect(() => {
    const rootEl_ = 'current' in rootEl ? rootEl.current : rootEl

    const targetEl_ = 'current' in targetEl ? targetEl.current : targetEl
    if (!targetEl_ || !rootEl_) {
      return
    }

    let rafId: number | undefined
    const loop = () => {
      const bbox = targetEl_.getBoundingClientRect()
      const point = getPointPosition(
        bbox,
        rootEl_.getBoundingClientRect(),
        targetPosition,
      )
      setTargetPoint((prev) => (prev && Point.eq(prev, point) ? prev : point))
      rafId = requestAnimationFrame(loop)
    }

    loop()

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [rootEl, targetEl, targetPosition])

  const path = useMemo(() => {
    if (!sourcePoint || !targetPoint) {
      return undefined
    }
    return createRoundedConnectorPath(sourcePoint, targetPoint, {
      direction,
      radius,
    })
  }, [direction, radius, sourcePoint, targetPoint])

  const attrs = useMemo((): React.SVGAttributes<SVGPathElement> => {
    if (variant === 'placeholder') {
      return {
        fill: 'none',
        stroke: 'var(--background-4)',
        strokeDasharray: '2 2',
        strokeWidth: 1,
      }
    }
    return {
      fill: 'none',
      stroke: 'var(--background-4)',
      strokeWidth: 1,
    }
  }, [variant])

  if (!path) {
    return
  }

  return <path d={path} {...attrs} />
}

const getPointPosition = (
  bbox: DOMRectReadOnly,
  rootBbox: DOMRectReadOnly,
  position: ConnectionPosition,
) => {
  return match(position)
    .with('bottom-middle', () => ({
      x: bbox.x + bbox.width / 2 - rootBbox.x,
      y: bbox.y + bbox.height - rootBbox.y,
    }))
    .with('top-middle', () => ({
      x: bbox.x + bbox.width / 2 - rootBbox.x,
      y: bbox.y - rootBbox.y,
    }))
    .with('center', () => ({
      x: bbox.x + bbox.width / 2 - rootBbox.x,
      y: bbox.y + bbox.height / 2 - rootBbox.y,
    }))
    .exhaustive()
}
